import React, { useContext, useRef, useState } from 'react';

import { ApiContext } from '_lib/api/apiContext';
import {
  AiCallForCallPopDTO,
  Company,
  isServiceTitanPortalV2Company,
  ServiceTitanMappedCall
} from '_lib/api/apiTypes.shim';
import { FlexBox } from '_lib/components/FlexBox/FlexBox';
import { Stack } from '_lib/components/Stack/Stack';

import { SERVICE_TITAN_ENTERPRISE_LOGIN_URL } from '_lib/global/config';
import { useAppDispatch } from '_lib/redux/hooks';
import { CallerIdState, PhoneCallURLState } from '_lib/types/PhoneCall.types';
import { isNonEmptyDnisArray } from '_lib/utils/helpers';
import { logDebug } from '_lib/utils/logging';

import { buildIFrameURL, getCallPopDnis } from '_lib/utils/urlStateUtils';

import { StPortalV2CompanyContext } from '../../common/CompanyPane/Company.slice';
import { CompanyPane } from '../../common/CompanyPane/CompanyPane';

import { CallDetailsBox } from './featureComponents/CallDetailsBox/CallDetailsBox';
import { CompanyZoneZipSelect } from './featureComponents/CompanyPane/CompanyZoneZipSelect/CompanyZoneZipSelect';

import { PortalIframePane } from './featureComponents/PortalIframePane/PortalIframePane';
import { normalizeTradeFromCompany, switchDifferentTenantCompany } from './StPortalPhoneCall.slice';

type StPortalPhoneCallProps = {
  companyContext: StPortalV2CompanyContext;
  phoneCallURLState: PhoneCallURLState;
  callerIdState: CallerIdState;
  aiCall: AiCallForCallPopDTO['aiCall'] | null;
  mappedCall: ServiceTitanMappedCall | null;
  handleSwitchCompany: (companyId: string) => Promise<Company | null>;
};

export const StPortalPhoneCall = (props: StPortalPhoneCallProps): React.ReactElement => {
  const { companyContext, phoneCallURLState, callerIdState, mappedCall, aiCall, handleSwitchCompany } = props;

  const validCallerId = callerIdState?.isValid && callerIdState.callerId;

  const dispatch = useAppDispatch();
  const api = useContext(ApiContext);
  const { data: appConfigData } = api.appConfig.useCachedAppConfigQuery();

  const refForDialog = useRef(null);
  const iframePaneRef = useRef(null);

  const [callHandleIframeUrl, setCallHandleIframeUrl] = useState<string>();
  const [iframeUrlPath, setIframeUrlPath] = useState<'login' | 'call handle'>('call handle');

  // as soon as 'appConfigData' is first available, build the iframe URL
  if (
    !callHandleIframeUrl &&
    validCallerId &&
    isNonEmptyDnisArray(companyContext.company.callCenter.dnisArray) &&
    appConfigData
  ) {
    setCallHandleIframeUrl(
      buildIFrameURL({
        callerId: validCallerId,
        companyDNIS: getCallPopDnis(companyContext.company.callCenter.dnisArray, phoneCallURLState.companyDNIS),
        providerOptions: companyContext.company.dataProvider.providerOptions,
        serviceTitanConfig: appConfigData.serviceTitanConfig
      })
    );
  }

  const handleSwitchStPortalV2Company = async (companyId: string) => {
    logDebug('// - Company Switch Handler ST Portal V2 - //');
    const newCompany = await handleSwitchCompany(companyId);
    if (!newCompany || !isServiceTitanPortalV2Company(newCompany)) return;

    if (
      newCompany.dataProvider.providerOptions.tenantId !== companyContext.company.dataProvider.providerOptions.tenantId
    ) {
      dispatch(switchDifferentTenantCompany(newCompany));
    } else {
      dispatch(normalizeTradeFromCompany(newCompany));
    }

    setIframeUrlPath('call handle');
    if (validCallerId && appConfigData && isNonEmptyDnisArray(newCompany.callCenter.dnisArray)) {
      setCallHandleIframeUrl(
        buildIFrameURL({
          callerId: validCallerId,
          companyDNIS: getCallPopDnis(newCompany.callCenter.dnisArray, phoneCallURLState.companyDNIS),
          providerOptions: newCompany.dataProvider.providerOptions,
          serviceTitanConfig: appConfigData.serviceTitanConfig
        })
      );
    } else {
      setCallHandleIframeUrl(undefined);
    }
  };

  return (
    <FlexBox sx={{ height: '100vh', width: '100%', flexDirection: 'column', overflow: 'hidden' }}>
      <Stack direction={'row'} spacing={0} sx={{ width: '100%', height: '100%', overflow: 'hidden' }}>
        <FlexBox sx={{ width: '60%', height: '100%', position: 'relative' }}>
          {isNonEmptyDnisArray(companyContext.company.callCenter.dnisArray) && !!validCallerId && (
            <PortalIframePane
              iframeURL={
                companyContext.company.dataProvider.providerOptions.isEnterprise && iframeUrlPath === 'login'
                  ? SERVICE_TITAN_ENTERPRISE_LOGIN_URL
                  : callHandleIframeUrl
              }
              iframePaneRef={iframePaneRef}
              companyId={companyContext.company.id}
              companyName={companyContext.company.name}
            />
          )}
        </FlexBox>
        <FlexBox
          sx={{ width: '40%', height: '100%', position: 'relative', flexDirection: 'column' }}
          ref={refForDialog}>
          <CompanyPane
            companyContext={companyContext}
            phoneCallURLState={phoneCallURLState}
            zipZoneOptions={{
              ZipZoneSelectComponent: CompanyZoneZipSelect
            }}
            iframeContext={{ callHandleIframeUrl, setIframeUrlPath, iframePaneRef }}
            aiCall={aiCall}
            handleSwitchCompany={handleSwitchStPortalV2Company}
            sx={{ visibility: 'visible', width: '100%', height: 'inherit' }}
          />
          <CallDetailsBox
            companyContext={companyContext}
            phoneCallURLState={phoneCallURLState}
            mappedCall={mappedCall}
            refForDialog={refForDialog}
            aiCallId={aiCall?.id}
          />
        </FlexBox>
      </Stack>
    </FlexBox>
  );
};
